import { safeLocalStorage } from "../../utils/safeLocalStorage";
import { useTenantScope } from "../../contexts/TenantScopeContext";
/**
 * Compatibility wrapper for useStytchMember that works in both:
 * - Cookie-based auth (normal app): Uses Stytch SDK directly
 * - Bearer token auth (iframe embed): Fetches from /api/ai/session/me and caches
 *
 * This allows the same code to work in both authentication modes.
 */

import { useEffect, useState } from "react";
import { useStytchMember } from "@stytch/react/b2b";
import { useEmbedAuth } from "../../contexts/EmbedAuthContext";
import { clearSessionMeCache, fetchSessionMe, getCachedSessionMe } from "./sessionMeCache";

const CACHE_KEY_MEMBER = "embed_member_cache";

interface Member {
    member_id: string;
    email_address: string;
    name: string;
    roles: Array<{ role_id: string }>;
}

interface MemberHook {
    member: Member | null;
    /**
     * False until the selected auth mode has resolved its member lookup.
     * A fresh page load starts with `member: null` for the first render or
     * two; a caller that redirects on `!member` (e.g. an admin-role check)
     * without waiting for this bounces an authenticated admin off the page
     * (see PrivateWrapper).
     */
    isInitialized: boolean;
}

function loadCachedMember(): Member | null {
    const sharedMember = getCachedSessionMe()?.member;
    if (sharedMember) return sharedMember;

    const cached = safeLocalStorage.getItem(CACHE_KEY_MEMBER);
    if (!cached) return null;
    try {
        return JSON.parse(cached) as Member;
    } catch (error) {
        console.error("[useCompatMember] Failed to parse cached member:", error);
        safeLocalStorage.removeItem(CACHE_KEY_MEMBER);
        return null;
    }
}

export function useCompatMember(): MemberHook {
    const tenantScope = useTenantScope();
    // Try Stytch SDK first (works in cookie-based auth)
    const stytchMember = useStytchMember();

    // Check embed auth context (works in Bearer token auth)
    const embedAuth = useEmbedAuth();

    // Seed synchronously from either cache so route guards never observe a
    // false loading gap when member data is already available.
    const [cachedMember, setCachedMember] = useState<Member | null>(loadCachedMember);
    const [resolvedSessionJwt, setResolvedSessionJwt] = useState<string | null>(null);

    // Embed mode: fetch from API if authenticated but no cache. Keyed on the
    // sessionJwt (not just isAuthenticated) so a fetch that raced a stale JWT
    // and 401'd is retried after the token refresh swaps in a fresh one —
    // same fix as useCompatOrganization. Goes through the shared
    // sessionMeCache so concurrent mounts (and useCompatOrganization's own
    // fetch) collapse onto a single in-flight request.
    const sessionJwt = embedAuth.tokens?.sessionJwt;
    useEffect(() => {
        if (embedAuth.isAuthenticated && sessionJwt && !cachedMember && !stytchMember.member) {
            let active = true;
            fetchSessionMe()
                .then((data) => {
                    if (!active) return;
                    const member = data.member;
                    setCachedMember(member);
                    safeLocalStorage.setItem(CACHE_KEY_MEMBER, JSON.stringify(member));
                })
                .catch((error) => {
                    if (!active) return;
                    console.error("[useCompatMember] Failed to fetch member:", error);
                })
                .finally(() => {
                    if (active) setResolvedSessionJwt(sessionJwt);
                });

            return () => {
                active = false;
            };
        }
    }, [embedAuth.isAuthenticated, sessionJwt, cachedMember, stytchMember.member]);

    // Clear cache when tokens expire or are cleared
    useEffect(() => {
        if (!embedAuth.isAuthenticated) {
            setCachedMember(null);
            setResolvedSessionJwt(null);
            safeLocalStorage.removeItem(CACHE_KEY_MEMBER);
            clearSessionMeCache();
        }
    }, [embedAuth.isAuthenticated]);

    // CONDITIONAL LOGIC AND RETURN AFTER ALL HOOKS

    if (tenantScope.mode === "canonical")
        return {
            member: tenantScope.sessionMember ?? null,
            isInitialized: !!tenantScope.sessionMember,
        };

    // If Stytch SDK has member data, use it (normal cookie-based flow)
    if (stytchMember.member) {
        return { member: stytchMember.member as Member, isInitialized: true };
    }

    if (embedAuth.isAuthenticated) {
        return {
            member: cachedMember,
            isInitialized: cachedMember !== null || resolvedSessionJwt === sessionJwt,
        };
    }

    // Cookie mode uses Stytch's own member-lookup readiness.
    return { member: null, isInitialized: stytchMember.isInitialized };
}
